import { FlaskConical } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

/**
 * PreviewBanner — issue #201's convention for pages that are not honest yet.
 *
 * ---------------------------------------------------------------------------
 * THE CONVENTION (read this before adding a fifth mock page)
 * ---------------------------------------------------------------------------
 *
 * Two situations get gated through this one component:
 *
 *   (a) A page presents FABRICATED DATA as if it were the signed-in user's
 *       real data (a friends list, a notification feed, a hand history —
 *       hardcoded arrays standing in for a feature with no real backend
 *       yet). Convention: the constant holding that data MUST be named
 *       `MOCK_<SOMETHING>` (all-caps, MOCK_ prefix), and the file that
 *       declares it MUST render <PreviewBanner /> above the content built
 *       from it.
 *
 *   (b) A page presents a FABRICATED OUTCOME — it claims an action
 *       succeeded (a toast, a confirmation) when nothing real happened.
 *       There is no data constant to name here, so nothing about this is
 *       mechanically detectable (see "what this does NOT catch" below).
 *       Convention: render <PreviewBanner /> anyway, so the page is honest
 *       *before* the user acts on it, and never emit a success signal that
 *       didn't happen.
 *
 * Enumeration commands (property 2 — "mechanical to find"), run from repo
 * root:
 *
 *   # Every page carrying fabricated DATA under this convention (case a):
 *   grep -rl '^const MOCK_' apps/smartpoker/src/pages
 *
 *   # Every page gated by this component at all (cases a + b):
 *   grep -rl '<PreviewBanner' apps/smartpoker/src/pages
 *
 * scripts/check_mock_data_gating.mjs automates the first list and asserts
 * every file it names also appears in the second list — i.e. no page can
 * declare MOCK_* data without rendering the banner. Run it directly:
 *
 *   node scripts/check_mock_data_gating.mjs
 *
 * (Not yet wired into scripts/check.sh — that file was out of territory for
 * #201; wiring it in as a "web" scope step, next to check_locale_parity.mjs,
 * needs sequencing by the epic owner.)
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS DOES NOT CATCH — the limits, stated plainly
 * ---------------------------------------------------------------------------
 *
 *  1. Naming discipline, not a language feature. A constant holding
 *     fabricated data that is NOT named `MOCK_*` (e.g. `sampleFriends`)
 *     evades both the banner requirement and the enumeration grep
 *     completely. Nothing at the type or runtime level forces the name.
 *
 *  2. Declaring-file only. If a MOCK_* constant is imported from a
 *     different module than the one that renders it, the check only
 *     inspects the file where it's *declared* — it does not trace imports
 *     to find where the data actually gets rendered.
 *
 *  3. Fabricated OUTCOMES (case b) are not mechanically enforced at all.
 *     Nothing greps for "this toast fires without a network call behind
 *     it." Support.tsx's bug — a success toast with nothing sent — is
 *     exactly this shape, and this script would not have caught it; it was
 *     found by reading the component. Property 2 (enumerable) only holds
 *     for the fabricated-DATA half of the convention; the fabricated-
 *     outcome half is human-reviewed, not machine-checked.
 *
 *  4. Presence, not honesty of copy. The check confirms <PreviewBanner />
 *     is rendered somewhere in the file — it does not read the strings
 *     passed to it. A banner with misleading titleKey/descriptionKey text
 *     would still pass.
 *
 *  5. Scoped to apps/smartpoker/src/pages/*.tsx. A mock page built as a
 *     modal, a feature-level component, or anything outside src/pages is
 *     invisible to both the grep and the script.
 *
 *  6. Textual match, not JSX-aware. The script looks for the literal
 *     substring "<PreviewBanner" anywhere in the file — including inside a
 *     comment. This is not hypothetical: while building this convention's
 *     D4 proof, a first draft of the throwaway offending page described,
 *     in a comment, what it was testing ("...rejects a page that ... does
 *     not render <PreviewBanner...") and the script reported it as passing
 *     — the comment's mention of the component name was enough to satisfy
 *     a plain-text search. A real AST check (parse the file, confirm a
 *     <PreviewBanner> JSX element actually exists) would close this gap;
 *     this script does not do that.
 */
interface PreviewBannerProps {
  /** i18n key for the banner headline. */
  titleKey: string;
  /** i18n key for the supporting copy. */
  descriptionKey: string;
}

export const PreviewBanner = ({ titleKey, descriptionKey }: PreviewBannerProps) => {
  const { t } = useTranslation();
  return (
    <Alert className="border-primary/40 bg-primary/5" data-testid="preview-banner">
      <FlaskConical className="h-4 w-4 text-primary" />
      <AlertTitle>{t(titleKey)}</AlertTitle>
      <AlertDescription>{t(descriptionKey)}</AlertDescription>
    </Alert>
  );
};
